import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { components } from '../utils/designTokens';

const getOptionValue = (option) => {
  if (option === undefined || option === null) return '';
  if (typeof option === 'string') return option;
  return String(option.value ?? option.label ?? '');
};

const getOptionLabel = (option) => {
  if (option === undefined || option === null) return '';
  if (typeof option === 'string') return option;
  return String(option.label ?? option.value ?? '');
};

const getOptionSearchText = (option) => {
  if (typeof option === 'string') return option;
  return [option?.label, option?.value, option?.searchText]
    .filter(Boolean)
    .join(' ');
};

const filterOptionType = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string,
    searchText: PropTypes.string,
  }),
]);

const FilterPill = ({ label, value, displayValue, filterKey, onRemove }) => (
  <div
    className={`flex items-center gap-1 ${components.pill.active.border} ${components.pill.base.rounded} px-2 py-1 transition-all`}
    style={components.pill.active.gradientStyle}
  >
    <span className="text-xs text-accent-secondary-base font-normal">{label}:</span>
    <span className="text-xs text-text-primary">{displayValue || value}</span>
    <button
      onClick={() => onRemove(filterKey, value)}
      className="ml-1 text-text-muted hover:text-text-primary text-xs leading-none"
      title="Remove filter"
    >
      ×
    </button>
  </div>
);

const AddFilterDropdown = ({ onAddFilter, availableFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    setInputValue('');
    setSuggestions(filter.options || []);
    setShowSuggestions(false);
  };

  const handleInputChange = (value) => {
    setInputValue(value);

    if (selectedFilter?.options && value.length > 0) {
      const filtered = selectedFilter.options
        .filter(option => getOptionSearchText(option).toLowerCase().includes(value.toLowerCase()))
        .slice(0, 8);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setSuggestions(selectedFilter?.options || []);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(getOptionLabel(suggestion));
    setShowSuggestions(false);
    handleAddFilter(getOptionValue(suggestion));
  };

  const handleAddFilter = (value) => {
    const finalValue = getOptionValue(value || inputValue).trim();
    if (selectedFilter && finalValue) {
      onAddFilter(selectedFilter.key, finalValue);
      setSelectedFilter(null);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
      setIsOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleAddFilter();
    } else if (e.key === 'Escape') {
      setSelectedFilter(null);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
      setIsOpen(false);
    }
  };

  const hasSelection = selectedFilter !== null || isOpen;
  const borderClass = hasSelection ? components.pill.active.border : components.pill.base.border;
  const gradientStyle = hasSelection ? components.pill.active.gradientStyle : components.pill.base.gradientStyle;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1 ${borderClass} ${components.pill.base.rounded} ${components.pill.base.padding} text-xs text-accent-primary-base hover:text-text-primary transition-all`}
        style={gradientStyle}
      >
        + Add Filter
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-background-primary border border-border-subtle rounded-lg shadow-lg z-20 w-80">
          {!selectedFilter ? (
            // Filter type selection
            <div className="py-1">
              {availableFilters.map(filter => (
                <button
                  key={filter.key}
                  onClick={() => handleFilterSelect(filter)}
                  className="block w-full text-left px-3 py-2 text-xs text-text-primary hover:bg-background-tertiary transition-colors"
                >
                  {filter.label}
                </button>
              ))}
            </div>
          ) : (
            // Value input with autocomplete
            <div className="p-3">
              {/* Header with title and back button */}
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs text-text-muted">
                  Add {selectedFilter.label} filter:
                </div>
                <button
                  onClick={() => setSelectedFilter(null)}
                  className="text-xs text-text-primary bg-accent-primary-base/10 border border-accent-primary-base/30 rounded px-2 py-1 hover:bg-accent-primary-base/15 font-medium"
                >
                  ← Back
                </button>
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={() => selectedFilter?.options && setShowSuggestions(true)}
                    placeholder={`Enter ${selectedFilter.label.toLowerCase()}`}
                    className="flex-1 bg-background-tertiary border border-border-subtle rounded px-2 py-1 text-xs text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base"
                    autoFocus
                  />
                  <button
                    onClick={() => handleAddFilter()}
                    disabled={!inputValue.trim()}
                    className="bg-accent-primary-base/10 border border-accent-primary-base/30 rounded px-2 py-1 text-xs text-text-primary hover:bg-accent-primary-base/15 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Add
                  </button>
                </div>

                {/* Autocomplete suggestions */}
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-background-primary border border-border-subtle rounded shadow-lg z-20 max-h-32 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={`${getOptionValue(suggestion)}-${index}`}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="block w-full text-left px-2 py-1 text-xs text-text-primary hover:bg-background-tertiary transition-colors"
                      >
                        {getOptionLabel(suggestion)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FilterPills = ({ activeFilters = [], availableFilters = [], onAddFilter, onRemoveFilter }) => {
  if (!onAddFilter || !onRemoveFilter) {
    throw new Error('FilterPills requires onAddFilter and onRemoveFilter props');
  }

  // For each filter type, exclude already-selected values from the options
  // but keep the filter type available to allow adding more values
  const availableFilterOptions = availableFilters.map(filter => {
    const activeValues = activeFilters
      .filter(active => active.key === filter.key)
      .map(active => active.value);

    // Filter out already-selected values from options.
    // Preserve undefined options (free-text filters) — they're always shown.
    const remainingOptions = filter.options !== undefined
      ? filter.options.filter(opt => !activeValues.includes(getOptionValue(opt)))
      : undefined;

    return {
      ...filter,
      options: remainingOptions
    };
  }).filter(filter => filter.options === undefined || filter.options.length > 0);

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Active filter pills */}
      {activeFilters.map((filter, index) => (
        <FilterPill
          key={`${filter.key}-${filter.value}-${index}`}
          label={filter.label}
          value={filter.value}
          displayValue={filter.displayValue}
          filterKey={filter.key}
          onRemove={onRemoveFilter}
        />
      ))}

      {/* Add filter dropdown - show if there are remaining options */}
      {availableFilterOptions.length > 0 && (
        <AddFilterDropdown
          onAddFilter={onAddFilter}
          availableFilters={availableFilterOptions}
        />
      )}
    </div>
  );
};

FilterPills.propTypes = {
  activeFilters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    displayValue: PropTypes.string,
  })),
  availableFilters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(filterOptionType),
  })).isRequired,
  onAddFilter: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
};

AddFilterDropdown.propTypes = {
  onAddFilter: PropTypes.func.isRequired,
  availableFilters: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(filterOptionType),
  })).isRequired,
};

export default FilterPills;
